import { z } from 'zod';
import { KNOWN_LOCALES, SUPPORTED_LOCALES } from '../constants/app.js';
import { countryName } from '../constants/international.js';
import { customFieldConfidentialitySchema, customFieldValueSchema, } from './custom-field.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { customFieldTypeSchema, documentTypeSchema } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
const knownLocaleSchema = z.enum([...KNOWN_LOCALES]);
/** Stammdatenfelder, die fachlich aus dem vorhandenen Kundenmodell ableitbar sind. */
export const CUSTOMER_HEADER_STANDARD_FIELDS = [
    'customerNumber',
    'companyName',
    'tradingName',
    'contact',
    'billingAddress',
    'shippingAddress',
    'email',
    'phone',
    'mobile',
    'vatId',
    'taxNumber',
    'datevDebtorAccount',
    'language',
    'currency',
    'paymentTerms',
    'deliveryTerms',
    'salesRepresentative',
    'priceGroup',
    'externalReference',
    'creditLimit',
    'creditRating',
    'dunningStatus',
    'deliveryBlock',
    'riskStatus',
    'creditInsurance',
];
export const customerHeaderStandardFieldSchema = z.enum(CUSTOMER_HEADER_STANDARD_FIELDS);
/** Diese Stammdaten dürfen unabhängig von einer manipulierten Layoutdefinition nie extern erscheinen. */
export const CUSTOMER_HEADER_SENSITIVE_STANDARD_FIELDS = [
    'creditLimit',
    'creditRating',
    'dunningStatus',
    'deliveryBlock',
    'riskStatus',
    'creditInsurance',
];
export const customerHeaderFieldSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('STANDARD'), field: customerHeaderStandardFieldSchema }).strict(),
    z.object({ kind: z.literal('CUSTOM_FIELD'), definitionId: idSchema }).strict(),
]);
export const customerHeaderVisibilitySchema = z
    .object({
    internal: z.boolean(),
    pdf: z.boolean(),
    portal: z.boolean(),
})
    .strict();
export const customerHeaderPresentationSchema = z
    .object({
    visible: z.boolean(),
    columns: z.number().int().min(1).max(4),
    density: z.enum(['COMPACT', 'DETAILED']),
})
    .strict();
export const customerHeaderSectionPresentationsSchema = z
    .object({
    internal: customerHeaderPresentationSchema,
    pdf: customerHeaderPresentationSchema,
    portal: customerHeaderPresentationSchema,
})
    .strict();
const localizedCustomerHeaderLabelsSchema = z
    .record(knownLocaleSchema, z.string().trim().min(1).max(120))
    .superRefine((labels, ctx) => {
    for (const locale of SUPPORTED_LOCALES) {
        if (labels[locale] === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [locale],
                message: `Für ${locale} fehlt eine Bezeichnung.`,
            });
        }
    }
});
export const customerHeaderConditionSchema = z
    .object({
    operator: z.enum(['ALWAYS', 'NON_EMPTY', 'EQUALS', 'NOT_EQUALS']),
    source: customerHeaderFieldSourceSchema.optional(),
    expectedValue: customFieldValueSchema.optional(),
})
    .strict()
    .superRefine((condition, ctx) => {
    if (condition.operator === 'ALWAYS') {
        if (condition.source !== undefined || condition.expectedValue !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'ALWAYS darf keine Vergleichswerte enthalten.',
            });
        }
        return;
    }
    if (condition.source === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['source'],
            message: 'Für die Bedingung fehlt das Vergleichsfeld.',
        });
    }
    if ((condition.operator === 'EQUALS' || condition.operator === 'NOT_EQUALS') &&
        condition.expectedValue === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expectedValue'],
            message: 'Für den Vergleich fehlt der Vergleichswert.',
        });
    }
});
export const customerHeaderSectionSchema = z
    .object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[a-zA-Z0-9:_-]+$/),
    labelTranslations: localizedCustomerHeaderLabelsSchema,
    sortOrder: z.number().int().min(0).max(10_000),
    defaultExpanded: z.boolean(),
    /** Verweist optional auf die bestehende Zusatzfeldkategorie statt eine zweite Kategorie anzulegen. */
    customFieldGroupId: idSchema.nullable(),
    presentations: customerHeaderSectionPresentationsSchema,
})
    .strict();
export const customerHeaderFieldConfigurationSchema = z
    .object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(140)
        .regex(/^[a-zA-Z0-9:_-]+$/),
    sectionId: z.string().trim().min(1).max(100),
    source: customerHeaderFieldSourceSchema,
    labelTranslations: localizedCustomerHeaderLabelsSchema,
    showLabel: z.boolean(),
    sortOrder: z.number().int().min(0).max(10_000),
    width: z.number().int().min(1).max(12),
    hideEmpty: z.boolean(),
    wrap: z.enum(['WRAP', 'TRUNCATE']),
    compactMaxCharacters: z.number().int().min(20).max(500),
    highlightInternal: z.boolean(),
    visibility: customerHeaderVisibilitySchema,
    condition: customerHeaderConditionSchema,
    fallbackValue: customFieldValueSchema.nullable(),
})
    .strict();
export const customerHeaderLayoutConfigurationSchema = z
    .object({
    sections: z.array(customerHeaderSectionSchema).max(50),
    fields: z.array(customerHeaderFieldConfigurationSchema).max(500),
})
    .strict()
    .superRefine((configuration, ctx) => {
    const sectionIds = new Set();
    for (const [index, section] of configuration.sections.entries()) {
        if (sectionIds.has(section.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['sections', index, 'id'],
                message: 'Bereichs-IDs müssen eindeutig sein.',
            });
        }
        sectionIds.add(section.id);
    }
    const fieldIds = new Set();
    const sourceKeys = new Set();
    for (const [index, field] of configuration.fields.entries()) {
        if (fieldIds.has(field.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fields', index, 'id'],
                message: 'Feld-IDs müssen eindeutig sein.',
            });
        }
        fieldIds.add(field.id);
        const sourceKey = field.source.kind === 'STANDARD'
            ? `standard:${field.source.field}`
            : `custom:${field.source.definitionId}`;
        if (sourceKeys.has(sourceKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fields', index, 'source'],
                message: 'Ein Stammdatenfeld darf nur einmal im Layout vorkommen.',
            });
        }
        sourceKeys.add(sourceKey);
        if (!sectionIds.has(field.sectionId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fields', index, 'sectionId'],
                message: 'Der zugeordnete Bereich existiert nicht.',
            });
        }
    }
});
export const customerHeaderLayoutSchema = z.object({
    id: idSchema.nullable(),
    documentType: documentTypeSchema,
    optimisticVersion: z.number().int().min(0),
    configuration: customerHeaderLayoutConfigurationSchema,
    isDefault: z.boolean(),
    updatedAt: isoDateTimeSchema.nullable(),
});
function rejectRetiredLayoutLabels(configuration, ctx) {
    for (const [index, section] of configuration.sections.entries()) {
        if (Object.hasOwn(section.labelTranslations, 'ru')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['configuration', 'sections', index, 'labelTranslations', 'ru'],
                message: 'Russisch ist keine aktive Sprache mehr.',
            });
        }
    }
    for (const [index, field] of configuration.fields.entries()) {
        if (Object.hasOwn(field.labelTranslations, 'ru')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['configuration', 'fields', index, 'labelTranslations', 'ru'],
                message: 'Russisch ist keine aktive Sprache mehr.',
            });
        }
    }
}
export const customerHeaderLayoutUpdateSchema = z
    .object({
    optimisticVersion: z.number().int().min(0),
    configuration: customerHeaderLayoutConfigurationSchema,
})
    .strict()
    .superRefine((input, ctx) => rejectRetiredLayoutLabels(input.configuration, ctx));
export const customerHeaderFieldKindSchema = z.enum([
    'TEXT',
    'NUMBER',
    'DECIMAL',
    'BOOLEAN',
    'DATE',
    'SELECT',
    'MULTI_SELECT',
    'ADDRESS',
    'EMAIL',
    'PHONE',
    'FORMULA',
    'WEEKLY_SCHEDULE',
]);
export const customerHeaderAvailableFieldSchema = z.object({
    key: z.string(),
    source: customerHeaderFieldSourceSchema,
    labelTranslations: localizedCustomerHeaderLabelsSchema,
    categoryId: z.string(),
    categoryTranslations: localizedCustomerHeaderLabelsSchema,
    customFieldGroupId: idSchema.nullable(),
    fieldKind: customerHeaderFieldKindSchema,
    customFieldType: customFieldTypeSchema.nullable(),
    confidentiality: customFieldConfidentialitySchema,
    externallyAllowed: z.boolean(),
    externalRestrictionReason: z.string().nullable(),
    defaultVisibility: customerHeaderVisibilitySchema,
    options: z.array(z.object({
        value: z.string().min(1).max(60),
        labelTranslations: localizedCustomerHeaderLabelsSchema,
    })),
});
export const customerHeaderAvailableFieldsQuerySchema = paginationQuerySchema.extend({
    documentType: documentTypeSchema,
    categoryId: z.string().trim().max(100).optional(),
    sourceKind: z.enum(['STANDARD', 'CUSTOM_FIELD']).optional(),
});
export const customerHeaderDocumentFieldsQuerySchema = customerHeaderAvailableFieldsQuerySchema.omit({
    documentType: true,
});
export const customerHeaderAvailableFieldsResponseSchema = z.object({
    data: z.array(customerHeaderAvailableFieldSchema),
    meta: paginationMetaSchema,
});
export const CUSTOMER_HEADER_VALUE_ORIGINS = [
    'MASTER_DATA',
    'DOCUMENT_VALUE',
    'INHERITED',
    'MANUAL_OVERRIDE',
    'BACKFILL_CURRENT_MASTER',
];
export const customerHeaderValueOriginSchema = z.enum(CUSTOMER_HEADER_VALUE_ORIGINS);
export const customerHeaderSnapshotValueSchema = z
    .object({
    fieldId: z.string().min(1).max(140),
    source: customerHeaderFieldSourceSchema,
    labelTranslations: localizedCustomerHeaderLabelsSchema,
    fieldKind: customerHeaderFieldKindSchema,
    confidentiality: customFieldConfidentialitySchema,
    externallyAllowed: z.boolean(),
    value: customFieldValueSchema,
    /** Historical PDF release; absent on legacy snapshots, which use their stored layout. */
    pdfAllowed: z.boolean().optional(),
    displayValues: z.record(knownLocaleSchema, z.string().max(10_000)),
    origin: customerHeaderValueOriginSchema,
    sourceRecordId: idSchema.nullable(),
    sourceUpdatedAt: isoDateTimeSchema.nullable(),
    inheritedFromDocumentId: idSchema.nullable(),
    manualOverride: z
        .object({
        userId: idSchema,
        changedAt: isoDateTimeSchema,
        previousOrigin: customerHeaderValueOriginSchema,
    })
        .strict()
        .nullable(),
})
    .strict();
export const customerHeaderSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    customerId: idSchema,
    capturedAt: isoDateTimeSchema,
    /** Gesetzt, wenn für einen Altbeleg nur der beim Backfill aktuelle Stammdatenstand bekannt war. */
    backfilledAt: isoDateTimeSchema.nullable(),
    values: z.array(customerHeaderSnapshotValueSchema).max(500),
})
    .strict();
export const customerHeaderRefreshChangeSchema = z.object({
    fieldId: z.string().min(1).max(140),
    labelTranslations: localizedCustomerHeaderLabelsSchema,
    snapshotValue: customerHeaderSnapshotValueSchema.nullable(),
    currentValue: customerHeaderSnapshotValueSchema,
    selectedByDefault: z.boolean(),
    canApply: z.boolean(),
    blockedReason: z.string().nullable(),
});
export const customerHeaderRefreshPreviewSchema = z.object({
    documentId: idSchema,
    documentVersion: z.number().int().min(1),
    customerUpdatedAt: isoDateTimeSchema,
    changes: z.array(customerHeaderRefreshChangeSchema).max(500),
});
export const customerHeaderRefreshApplySchema = z
    .object({
    version: z.number().int().min(1),
    decisions: z
        .array(z
        .object({
        fieldId: z.string().min(1).max(140),
        apply: z.boolean(),
    })
        .strict())
        .max(500),
})
    .strict()
    .superRefine((input, ctx) => {
    const seen = new Set();
    for (const [index, decision] of input.decisions.entries()) {
        if (seen.has(decision.fieldId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['decisions', index, 'fieldId'],
                message: 'Jedes Feld darf nur einmal entschieden werden.',
            });
        }
        seen.add(decision.fieldId);
    }
});
const customerHeaderDocumentValueOperationSchema = z.discriminatedUnion('action', [
    z.object({ fieldId: z.string().min(1).max(140), action: z.literal('KEEP') }).strict(),
    z.object({ fieldId: z.string().min(1).max(140), action: z.literal('RESET_TO_MASTER') }).strict(),
    z
        .object({
        fieldId: z.string().min(1).max(140),
        action: z.literal('OVERRIDE'),
        value: customFieldValueSchema,
    })
        .strict(),
]);
export const customerHeaderDocumentOverrideUpdateSchema = z
    .object({
    version: z.number().int().min(1),
    configuration: customerHeaderLayoutConfigurationSchema,
    valueOperations: z.array(customerHeaderDocumentValueOperationSchema).max(500),
})
    .strict()
    .superRefine((input, ctx) => {
    rejectRetiredLayoutLabels(input.configuration, ctx);
    const layoutIds = new Set(input.configuration.fields.map((field) => field.id));
    const seen = new Set();
    for (const [index, operation] of input.valueOperations.entries()) {
        if (!layoutIds.has(operation.fieldId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['valueOperations', index, 'fieldId'],
                message: 'Die Wertänderung verweist auf kein Feld des Beleglayouts.',
            });
        }
        if (seen.has(operation.fieldId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['valueOperations', index, 'fieldId'],
                message: 'Pro Feld ist nur eine Wertänderung zulässig.',
            });
        }
        seen.add(operation.fieldId);
    }
});
export const customerHeaderAuditQuerySchema = paginationQuerySchema.pick({
    page: true,
    pageSize: true,
});
export const customerHeaderAuditEntrySchema = z.object({
    id: idSchema,
    occurredAt: isoDateTimeSchema,
    actorName: z.string().nullable(),
    action: z.enum(['CREATE', 'UPDATE']),
    changeType: z.enum([
        'SNAPSHOT_CAPTURED',
        'MASTER_DATA_REFRESHED',
        'DOCUMENT_OVERRIDE_SAVED',
        'DOCUMENT_FIELDS_SYNCHRONIZED',
    ]),
    changedFieldCount: z.number().int().min(0),
    externalPresentationChanged: z.boolean(),
});
export const customerHeaderAuditResponseSchema = z.object({
    data: z.array(customerHeaderAuditEntrySchema),
    meta: paginationMetaSchema,
});
export function customerHeaderSourceKey(source) {
    return source.kind === 'STANDARD' ? `standard:${source.field}` : `custom:${source.definitionId}`;
}
function isEmptyCustomerHeaderValue(value) {
    return value === null || value === '' || (Array.isArray(value) && value.length === 0);
}
function fallbackDisplayValue(value, locale) {
    if (value === null)
        return '';
    if (Array.isArray(value))
        return value.join(', ');
    if (typeof value === 'boolean') {
        const translated = {
            de: ['Ja', 'Nein'],
            en: ['Yes', 'No'],
            tr: ['Evet', 'Hayır'],
            fr: ['Oui', 'Non'],
            es: ['Sí', 'No'],
            ru: ['Да', 'Нет'],
        };
        return value ? translated[locale][0] : translated[locale][1];
    }
    return String(value);
}
function addressCountryDisplay(value, source, locale) {
    if (source.kind !== 'STANDARD' ||
        (source.field !== 'billingAddress' && source.field !== 'shippingAddress')) {
        return value;
    }
    // Format legacy address snapshots without changing their stored values.
    return value.replace(/(^|\n)([A-Z]{2})(\s*)$/u, (_match, prefix, code, suffix) => `${prefix}${countryName(code, locale)}${suffix}`);
}
/**
 * Gemeinsame, rein funktionale Auflösung für ERP, PDF und Portal. Externe
 * Kontexte besitzen hier eine zweite unveränderliche Vertraulichkeitsschranke,
 * selbst wenn eine gespeicherte Layoutdefinition manipuliert worden wäre.
 */
export function resolveCustomerHeaderSections(input) {
    const valuesBySource = new Map(input.snapshot.values.map((entry) => [customerHeaderSourceKey(entry.source), entry]));
    const valueFor = (source) => valuesBySource.get(customerHeaderSourceKey(source)) ?? null;
    const conditionMatches = (field) => {
        if (field.condition.operator === 'ALWAYS')
            return true;
        if (field.condition.source === undefined)
            return false;
        const compared = valueFor(field.condition.source)?.value ?? null;
        if (field.condition.operator === 'NON_EMPTY')
            return !isEmptyCustomerHeaderValue(compared);
        const equal = JSON.stringify(compared) === JSON.stringify(field.condition.expectedValue);
        return field.condition.operator === 'EQUALS' ? equal : !equal;
    };
    return [...input.layout.sections]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .flatMap((section) => {
        const presentation = section.presentations[input.context];
        if (!presentation.visible)
            return [];
        const fields = input.layout.fields
            .filter((field) => field.sectionId === section.id)
            .sort((left, right) => left.sortOrder - right.sortOrder)
            .flatMap((field) => {
            if (!field.visibility[input.context] || !conditionMatches(field))
                return [];
            const entry = valueFor(field.source);
            if (input.context !== 'internal') {
                const sensitiveStandard = field.source.kind === 'STANDARD' &&
                    CUSTOMER_HEADER_SENSITIVE_STANDARD_FIELDS.includes(field.source.field);
                if (entry === null ||
                    sensitiveStandard ||
                    !entry.externallyAllowed ||
                    entry.confidentiality !== 'PUBLIC') {
                    return [];
                }
            }
            const effectiveValue = entry === null || isEmptyCustomerHeaderValue(entry.value)
                ? field.fallbackValue
                : entry.value;
            const isEmpty = isEmptyCustomerHeaderValue(effectiveValue);
            if (field.hideEmpty && isEmpty)
                return [];
            const displayValue = entry !== null && !isEmptyCustomerHeaderValue(entry.value)
                ? (entry.displayValues[input.locale] ?? entry.displayValues.en ?? '')
                : fallbackDisplayValue(field.fallbackValue, input.locale);
            return [
                {
                    configuration: field,
                    snapshot: entry,
                    label: field.labelTranslations[input.locale] ?? field.labelTranslations.en ?? field.id,
                    displayValue: addressCountryDisplay(displayValue, field.source, input.locale),
                    isEmpty,
                },
            ];
        });
        if (fields.length === 0)
            return [];
        return [
            {
                configuration: section,
                label: section.labelTranslations[input.locale] ?? section.labelTranslations.en ?? section.id,
                presentation,
                fields,
            },
        ];
    });
}
//# sourceMappingURL=customer-header.js.map